import React, { useState, useEffect, useCallback } from "react";
import { notification } from "antd";
import { apiRequest } from "util/services";
import { UNLOGGED } from "util/constants";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import Representation from "./Representation";
import { useTranslation } from "react-i18next";
import { fetchAlertLimits } from "util/alertLimits";
import { fetchPurchaseHistory } from "util/purchaseHistory";
import {
  canUpdateOrganizationAiCredits,
  normalizeSubscriptionAllResponse,
} from "util/organizationRoles";
import { buildContactTicketPrefill } from "util/planEntitlements";
import { isEnterpriseCustomer } from "util/platformPricing";
import SonicSurveyUpgradeModal from "pages/SurveyManagement/SurveyUpgradeModal";
import useSurveyCheckoutReturn from "hooks/useSurveyCheckoutReturn";

const SURVEY_CHECKOUT_KEYS = new Set([
  "survey_plus",
  "survey_pro",
  "survey_pro_plus",
]);

const Plan = ({ authenticated, user }) => {
  const history = useHistory();
  const location = useLocation();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [entitlementsMe, setEntitlementsMe] = useState(null);
  const [entitlementsLoading, setEntitlementsLoading] = useState(true);
  const [entitlementsError, setEntitlementsError] = useState(null);
  const [alertLimits, setAlertLimits] = useState(null);
  const [alertLimitsLoading, setAlertLimitsLoading] = useState(true);
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [purchaseHistoryTotal, setPurchaseHistoryTotal] = useState(0);
  const [purchaseHistoryLoading, setPurchaseHistoryLoading] = useState(false);
  const [appSumoCode, setAppSumoCode] = useState("");
  const [isTicketModalVisible, setIsTicketModalVisible] = useState(false);
  const [isTicketModalLoading, setIsTicketModalLoading] = useState(false);
  const [ticketText, setTicketText] = useState("");
  const [isReedemingAppsumoButton, setIsReedemingAppsumoButton] = useState(
    false
  );
  const [aiCreditsModalOpen, setAiCreditsModalOpen] = useState(false);
  const [aiCreditsDraft, setAiCreditsDraft] = useState(0);
  const [aiCreditsSaving, setAiCreditsSaving] = useState(false);
  const [surveyUpgradeOpen, setSurveyUpgradeOpen] = useState(false);
  const [surveyUpgradeMode, setSurveyUpgradeMode] = useState("plus");
  const { t } = useTranslation();
  const sessionRoles = useSelector((state) => state.userReducer);
  const getSubscribtionStatus = useCallback(() => {
    const payload = {
      user_id: user.uid,
    };
    apiRequest("subscriptions/all", "POST", payload, true).then((result) => {
      const subscription = normalizeSubscriptionAllResponse(result);
      if (subscription) {
        setSubscriptionStatus(subscription);
      } else if (result?.error) {
        notification.error({
          message: result.error,
        });
      }
    });
  }, [user.uid]);

  const loadEntitlementsMe = useCallback(async () => {
    setEntitlementsLoading(true);
    setEntitlementsError(null);
    let result = await apiRequest("entitlements/me", "GET", null, true);
    if (!result?.success) {
      result = await apiRequest("entitlements/me", "POST", {}, true);
    }
    if (result?.success) {
      setEntitlementsMe(result.success);
    } else {
      setEntitlementsError(
        result?.error ||
          t(
            "settings.plan_entitlements_error",
            "Plan verileri yüklenemedi. Sayfayı yenileyin veya destek ile iletişime geçin.",
          ),
      );
      if (result?.error) {
        notification.error({ message: result.error });
      }
    }
    setEntitlementsLoading(false);
  }, [t]);

  const refreshPlan = useCallback(() => {
    getSubscribtionStatus();
    loadEntitlementsMe();
  }, [getSubscribtionStatus, loadEntitlementsMe]);

  useSurveyCheckoutReturn({ user, onRefresh: refreshPlan });

  const loadAlertLimits = useCallback(async () => {
    setAlertLimitsLoading(true);
    const limits = await fetchAlertLimits();
    setAlertLimits(limits);
    setAlertLimitsLoading(false);
  }, []);

  const loadPurchaseHistory = useCallback(async () => {
    setPurchaseHistoryLoading(true);
    const data = await fetchPurchaseHistory({ limit: 30, offset: 0 });
    if (data) {
      setPurchaseHistory(data.purchases);
      setPurchaseHistoryTotal(data.total);
    }
    setPurchaseHistoryLoading(false);
  }, []);

  useEffect(() => {
    getSubscribtionStatus();
    loadAlertLimits();
    loadEntitlementsMe();
  }, [getSubscribtionStatus, loadAlertLimits, loadEntitlementsMe]);

  useEffect(() => {
    if (subscriptionStatus?.WorkspaceContentEditable) {
      loadPurchaseHistory();
    }
  }, [subscriptionStatus?.WorkspaceContentEditable, loadPurchaseHistory]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const purchase = params.get("alert_purchase");
    if (!purchase) return;
    if (purchase === "success") {
      notification.success({
        message: t(
          "alerts_page.purchase_success",
          "Aylık add-on etkinleştirildi (+10 KPI ve +10 alarm).",
        ),
      });
      loadAlertLimits();
      loadPurchaseHistory();
    } else if (purchase === "cancelled") {
      notification.info({
        message: t("alerts_page.purchase_cancelled", "Abonelik iptal edildi."),
      });
    }
    params.delete("alert_purchase");
    const nextSearch = params.toString();
    history.replace({
      pathname: location.pathname,
      search: nextSearch ? `?${nextSearch}` : "",
    });
  }, [
    location.search,
    location.pathname,
    history,
    t,
    loadAlertLimits,
    loadPurchaseHistory,
  ]);

  useEffect(() => {
    setSubscriptionStatus(subscriptionStatus);
  }, [subscriptionStatus]);

  useEffect(() => {
    if (authenticated === UNLOGGED || user.plan === undefined) {
      history.push("/");
    }
  }, [subscriptionStatus, authenticated, user.plan, history]);

  const handleAppsumoMultipleCoupons = async () => {
    if (isReedemingAppsumoButton) {
      return;
    }
    setIsReedemingAppsumoButton(true);
    const result = await apiRequest(
      "subscriptions/appsumo/multiple_coupons",
      "POST",
      {
        user_id: user.uid,
        code: appSumoCode,
      },
      true
    );

    if (result.success) {
      notification.success({
        message: t("notifications.122"),
      });
    } else {
      notification.error({
        message: result.error,
      });
    }
    setIsReedemingAppsumoButton(false);
  };

  const handeAppsumoCoupon = async () => {
    if (isReedemingAppsumoButton) {
      return;
    }

    setIsReedemingAppsumoButton(true);
    const result = await apiRequest(
      "subscriptions/appsumo/second_coupon",
      "POST",
      {
        user_id: user.uid,
        code: appSumoCode,
      },
      true
    );

    if (result.success) {
      window.location.reload(false);
    } else {
      notification.error({
        message: result.error,
      });
    }
    setIsReedemingAppsumoButton(false);
  };

  const openContactRequest = ({ productKey, productName }) => {
    if (
      SURVEY_CHECKOUT_KEYS.has(productKey) &&
      !isEnterpriseCustomer(subscriptionStatus)
    ) {
      const mode =
        productKey === "survey_pro_plus"
          ? "pro_plus"
          : productKey === "survey_pro"
            ? "pro"
            : "plus";
      setSurveyUpgradeMode(mode);
      setSurveyUpgradeOpen(true);
      return;
    }
    setTicketText(
      buildContactTicketPrefill({
        productKey,
        productName,
        t,
      }),
    );
    setIsTicketModalVisible(true);
  };

  const submitTicket = async () => {
    if (ticketText.length === 0) {
      return;
    }

    setIsTicketModalLoading(true);

    const result = await apiRequest(
      "subscriptions/post_support_ticket",
      "POST",
      {
        content: ticketText,
        firebase_id: user.uid,
      },
      true
    );

    if (result.success) {
      notification.success({
        message: t("notifications.123"),
      });
    } else {
      notification.error({
        message: result.error,
      });
    }

    setTicketText("");
    setIsTicketModalVisible(false);
    setIsTicketModalLoading(false);
  };

  const saveAiCredits = async () => {
    const nextRemaining = Number(aiCreditsDraft);
    if (!Number.isFinite(nextRemaining) || nextRemaining < 0) {
      notification.error({
        message: t("common.error"),
        description: t("settings.plan_ai_credits_invalid"),
      });
      return;
    }

    setAiCreditsSaving(true);
    try {
      const result = await apiRequest(
        "welcome/power-user/ai-credits",
        "POST",
        { ai_credits_remaining: Math.floor(nextRemaining) },
        true,
      );
      if (result?.error) {
        notification.error({
          message: t("common.error"),
          description: result.error,
        });
        return;
      }
      if (result?.success) {
        const saved = result.success;
        notification.success({
          message: t("settings.plan_ai_credits_saved"),
        });
        setAiCreditsModalOpen(false);
        setSubscriptionStatus((prev) => ({
          ...prev,
          RemainingAiCredits:
            saved?.ai_credits_remaining ?? prev?.RemainingAiCredits ?? 0,
          AiCreditsTotal: saved?.ai_credits_total ?? prev?.AiCreditsTotal,
          AiCreditsUsed: saved?.ai_credits_used ?? prev?.AiCreditsUsed,
        }));
        getSubscribtionStatus();
      }
    } finally {
      setAiCreditsSaving(false);
    }
  };

  if (subscriptionStatus !== null) {
    const canManagePurchases = Boolean(subscriptionStatus?.WorkspaceContentEditable);
    const canUpdateAiCredits =
      canUpdateOrganizationAiCredits(subscriptionStatus) ||
      canUpdateOrganizationAiCredits(sessionRoles);
    return (
      <>
      <Representation
        plan={subscriptionStatus}
        entitlementsMe={entitlementsMe}
        entitlementsLoading={entitlementsLoading}
        entitlementsError={entitlementsError}
        alertLimits={alertLimits}
        alertLimitsLoading={alertLimitsLoading}
        canManagePurchases={canManagePurchases}
        purchaseHistory={purchaseHistory}
        purchaseHistoryTotal={purchaseHistoryTotal}
        purchaseHistoryLoading={purchaseHistoryLoading}
        onRefreshPurchaseHistory={loadPurchaseHistory}
        appSumoCode={appSumoCode}
        setAppSumoCode={setAppSumoCode}
        handeAppsumoCoupon={handeAppsumoCoupon}
        handleAppsumoMultipleCoupons={handleAppsumoMultipleCoupons}
        isReedemingAppsumoButton={isReedemingAppsumoButton}
        isTicketModalLoading={isTicketModalLoading}
        isTicketModalVisible={isTicketModalVisible}
        setIsTicketModalVisible={setIsTicketModalVisible}
        ticketText={ticketText}
        setTicketText={setTicketText}
        submitTicket={submitTicket}
        onOpenContactRequest={openContactRequest}
        canUpdateAiCredits={canUpdateAiCredits}
        aiCreditsModalOpen={aiCreditsModalOpen}
        setAiCreditsModalOpen={setAiCreditsModalOpen}
        aiCreditsDraft={aiCreditsDraft}
        setAiCreditsDraft={setAiCreditsDraft}
        onSaveAiCredits={saveAiCredits}
        aiCreditsSaving={aiCreditsSaving}
        user={user}
      />
      <SonicSurveyUpgradeModal
        open={surveyUpgradeOpen}
        onClose={() => setSurveyUpgradeOpen(false)}
        mode={surveyUpgradeMode}
        returnPath="/settings/plan"
        user={user}
        onSuccess={refreshPlan}
      />
      </>
    );
  } else {
    return <div></div>;
  }
};

export default Plan;
